import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import GlassmorphismTokens from '../../styles/GlassmorphismTokens';
import { DarkModalOverlay } from '../shared/GlassCard';
import { SOUL_QUIZ_QUESTIONS, computePsychographicProfile, deriveTravelSoul } from '../../data/psychographicData';
import haptic from '../../utils/haptics';

const Container = styled(DarkModalOverlay)`
  flex-direction: column;
  padding: 24px;
`;

const QuizCard = styled(motion.div)`
  max-width: 480px;
  width: 100%;
  background: ${GlassmorphismTokens.glass.medium};
  backdrop-filter: ${GlassmorphismTokens.blur.lg};
  -webkit-backdrop-filter: ${GlassmorphismTokens.blur.lg};
  border: ${GlassmorphismTokens.border.medium};
  border-radius: 24px;
  padding: 32px 24px;
  box-shadow: ${GlassmorphismTokens.shadow.glass};

  @supports not (backdrop-filter: blur(1px)) {
    background: rgba(40, 40, 60, 0.85);
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  margin-bottom: 32px;
  overflow: hidden;
`;

const ProgressFill = styled(motion.div)`
  height: 100%;
  background: ${GlassmorphismTokens.gradient.blueCTA};
  border-radius: 2px;
`;

const Scenario = styled.p`
  font-size: 18px;
  font-weight: 600;
  color: white;
  line-height: 1.5;
  margin: 0 0 24px 0;
  text-align: center;
`;

const ChoicesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ChoiceButton = styled(motion.button)`
  width: 100%;
  padding: 16px 20px;
  border-radius: 16px;
  border: 1.5px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.08);
  color: white;
  font-size: 15px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  line-height: 1.4;

  &:hover {
    border-color: rgba(66, 133, 244, 0.5);
    background: rgba(66, 133, 244, 0.12);
  }
`;

const QuestionCounter = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  text-align: center;
  margin-bottom: 16px;
`;

const TravelSoulQuiz = ({ onComplete, onClose }) => {
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});

  const handleChoice = useCallback((choice) => {
    haptic.light();
    const q = SOUL_QUIZ_QUESTIONS[currentQ];
    const newAnswers = { ...answers, [q.id]: choice };
    setAnswers(newAnswers);

    if (currentQ + 1 >= SOUL_QUIZ_QUESTIONS.length) {
      // Compute and save
      const psychographic = computePsychographicProfile(newAnswers);
      const travelSoul = deriveTravelSoul(psychographic);

      // Merge into existing travelDNA
      try {
        const existing = JSON.parse(localStorage.getItem('travelDNA') || '{}');
        const merged = {
          ...existing,
          psychographic,
          travelSoul,
        };
        localStorage.setItem('travelDNA', JSON.stringify(merged));
      } catch { /* ignore */ }

      haptic.success();
      if (onComplete) onComplete({ psychographic, travelSoul });
    } else {
      setCurrentQ(prev => prev + 1);
    }
  }, [currentQ, answers, onComplete]);

  const question = SOUL_QUIZ_QUESTIONS[currentQ];
  const progress = ((currentQ) / SOUL_QUIZ_QUESTIONS.length) * 100;

  return (
    <Container
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <QuizCard>
        <ProgressBar>
          <ProgressFill
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </ProgressBar>

        <AnimatePresence mode="wait">
          <motion.div
            key={question.id}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.25 }}
          >
            <QuestionCounter>
              Question {currentQ + 1} of {SOUL_QUIZ_QUESTIONS.length}
            </QuestionCounter>

            <Scenario>{question.scenario}</Scenario>

            <ChoicesContainer>
              <ChoiceButton
                onClick={() => handleChoice('A')}
                whileTap={{ scale: 0.97 }}
              >
                {question.choiceA.text}
              </ChoiceButton>
              <ChoiceButton
                onClick={() => handleChoice('B')}
                whileTap={{ scale: 0.97 }}
              >
                {question.choiceB.text}
              </ChoiceButton>
            </ChoicesContainer>
          </motion.div>
        </AnimatePresence>
      </QuizCard>

      {onClose && (
        <SkipButton onClick={onClose}>Skip for now</SkipButton>
      )}
    </Container>
  );
};

const SkipButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.4);
  font-size: 14px;
  cursor: pointer;
  margin-top: 20px;
  padding: 8px 16px;

  &:hover {
    color: rgba(255, 255, 255, 0.7);
  }
`;

export default TravelSoulQuiz;
